import { api, type Api } from "@api/bot-base-api-request";
import { useMutation } from "@tanstack/react-query";
import {
  NotificationParams,
  useNotification,
} from "@providers/notifications-bot-actions/notifications-bot-actions.tsx";

const BOT_SELL_TOKEN_QUERY_KEY = "bot-sell-token";

type BotSellTokenParams = {
  appPubKey: string;
  userId: string;
  chainId: number;
  tokenAddress: string;
  walletAddress: string;
  amount: string | number;
  slippage?: number;
};

type BotSellTokenInput = {
  params: BotSellTokenParams;
  notifyParams: NotificationParams;
};

type BotSellTokenResponse = {
  txHash: string | null;
  isSuccess: boolean;
  errMessage?: unknown;
  pnl?: number;
  roi?: number;
  currentBalance?: number;
  duration?: string;
};

const postBotSellToken = async (
  api: Api,
  params: BotSellTokenParams
): Promise<BotSellTokenResponse> =>
  api.post("trade/sell", { json: params }).json();

const useBotSellTokenMutation = () => {
  const { setNotifyProps } = useNotification();

  return useMutation<BotSellTokenResponse, Error, BotSellTokenInput>({
    mutationFn: async (input: BotSellTokenInput) =>
      postBotSellToken(api, input.params),
    mutationKey: [BOT_SELL_TOKEN_QUERY_KEY],
    onSuccess: async (_data, variables) => {
      const params = inputToNotificationParams(variables);

      if (_data.isSuccess) {
        setNotifyProps({
          type: "success",
          action: "sell",
          params: {
            ...params,
            pnl: _data.pnl,
            roi: _data.roi,
            currentBalance: _data.currentBalance,
            duration: _data.duration,
          },
        });
      } else {
        setNotifyProps({
          type: "error",
          action: "sell",
          params,
        });
      }
    },
    onError: async (_error, variables) => {
      setNotifyProps({
        type: "error",
        action: "sell",
        params: inputToNotificationParams(variables),
      });
    },
  });
};

const inputToNotificationParams = (
  inputParams: BotSellTokenInput
): NotificationParams => {
  const params: NotificationParams = {
    youSell: inputParams.notifyParams.youSell,
    tokenSymbol: inputParams.notifyParams.tokenSymbol,
    youReceive: inputParams.notifyParams.youReceive,
    chainSymbol: inputParams.notifyParams.chainSymbol,
  };
  return params;
};

export { useBotSellTokenMutation, postBotSellToken, BOT_SELL_TOKEN_QUERY_KEY };
